import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Badge,
    Table,
    Text,
    TextInput,
    Select,
    Loader,
} from '@mantine/core';
import { IconSearch, IconChevronUp, IconChevronDown, IconSelector } from '@tabler/icons-react';
import styled from 'styled-components';
import {getDashboardData, getProperties, getPropertyData} from '../../../store/reducers/propertyManager';
import Spinner from "../../../components/Spinner/v1/Spinner";

const MOCK_PROPERTIES = [
    { id: 1, name: 'Central Park Apts', units: 85, subscribers: 25 },
    { id: 2, name: 'Harbor View', units: 85, subscribers: 25 },
    { id: 3, name: 'Skyline Towers', units: 85, subscribers: 25 },
];

const MOCK_UNITS = {
    'Central Park Apts': [
        { id: 1, propertyName: 'Central Park Apts', unitNumber: '#01', status: 'Active', activeSince: 'Aug 1, 2024', durationActive: '8 months' },
        { id: 2, propertyName: 'Central Park Apts', unitNumber: '#22', status: 'Active', activeSince: 'Aug 1, 2024', durationActive: '6 months' },
        { id: 3, propertyName: 'Central Park Apts', unitNumber: '#33', status: 'Paused', activeSince: 'Aug 1, 2024', durationActive: '4 months' },
        { id: 4, propertyName: 'Central Park Apts', unitNumber: '#37', status: 'Active', activeSince: 'Oct 1, 2024', durationActive: '6 months' },
        { id: 5, propertyName: 'Central Park Apts', unitNumber: '#44', status: 'Paused', activeSince: 'Aug 1, 2024', durationActive: '13 months' },
        { id: 6, propertyName: 'Central Park Apts', unitNumber: '#55', status: 'Active', activeSince: 'Aug 1, 2024', durationActive: '6 months' },
        { id: 7, propertyName: 'Central Park Apts', unitNumber: '#66', status: 'Paused', activeSince: 'Aug 1, 2024', durationActive: '13 months' },
    ],
};

const STATUS_COLORS = {
    ACTIVE: 'green',
    ONBOARDING: 'yellow',
    CANCELED: 'gray',
};

const Portfolio = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [filterByProperty, setFilterByProperty] = useState('All');

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const properties = useSelector((s) => s.propertyManager.propertyInfo) || [];
    const customerData = useSelector((s) => s.propertyManager.propertyData) || [];

    const handleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig.key || !customerData.length) return customerData;

        return [...customerData].sort((a, b) => {
            let aVal, bVal;

            if (sortConfig.key === 'unitNumber') {
                // Strip '#' and sort numerically
                aVal = parseInt(a.unitNumber?.replace(/\D/g, ''), 10) || 0;
                bVal = parseInt(b.unitNumber?.replace(/\D/g, ''), 10) || 0;
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            if (sortConfig.key === 'subscriptionStartDate') {
                aVal = new Date(a.subscriptionStartDate || 0).getTime();
                bVal = new Date(b.subscriptionStartDate || 0).getTime();
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            if (sortConfig.key === 'monthsSince') {
                aVal = monthsPassed(a.subscriptionStartDate);
                bVal = monthsPassed(b.subscriptionStartDate);
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // Default: alphabetical (customerName, status)
            aVal = (a[sortConfig.key] || '').toString().toLowerCase();
            bVal = (b[sortConfig.key] || '').toString().toLowerCase();
            const cmp = aVal.localeCompare(bVal);
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [customerData, sortConfig]);

    const SortIcon = ({ colKey }) => {
        if (sortConfig.key !== colKey) return <IconSelector size={14} color="#adb5bd" />;
        return sortConfig.direction === 'asc'
            ? <IconChevronUp size={14} color="#1a1a2e" />
            : <IconChevronDown size={14} color="#1a1a2e" />;
    };

    console.log("customerData", customerData);

    useEffect(() => {
        setIsLoading(true);
        dispatch(getDashboardData({ setLoading: setIsLoading }));
    }, [dispatch]);

    const filtered = properties.filter((p) =>
        p.propertyName.toLowerCase().includes(search.toLowerCase())
    );

    function callForPropertyDetails(property) {
        setSelectedProperty(property)
        setIsLoading(true);
        dispatch(getPropertyData({
            setLoading: setIsLoading,
            propertyId: property.propertyId,
        }));
    }

    const detailStats = selectedProperty
        ? {
              activeUnits: customerData.filter((u) => u.status === 'ACTIVE').length,
              totalUnits: selectedProperty.totalUnitCount,
              subscribers: selectedProperty.totalSubscriberCount,
              penetrationRate: selectedProperty.totalUnitCount
                  ? ((selectedProperty.totalSubscriberCount / selectedProperty.totalUnitCount) * 100).toFixed(2)
                  : 0,
          }
        : null;

    function monthsPassed(dateString) {
        const past = new Date(dateString);
        const now = new Date();

        const years = now.getFullYear() - past.getFullYear();
        const months = now.getMonth() - past.getMonth();
        let total = years * 12 + months;

        // Subtract 1 if we haven't reached the same day-of-month yet
        if (now.getDate() < past.getDate()) {
            total -= 1;
        }

        return total;
    }


    return (
        <PageWrapper>
            <PageHeader>
                <Text fw={800} fz={28} tt="uppercase" c="#1a1a2e">
                    Portfolio
                </Text>
            </PageHeader>

            {isLoading && (
                <Spinner />
            )}

            <ContentLayout>
                <PropertyList>
                    <SearchWrapper>
                        <TextInput
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.currentTarget.value)}
                            leftSection={<IconSearch size={16} stroke={1.5} />}
                            styles={{ input: { borderRadius: '6px' } }}
                        />
                    </SearchWrapper>

                    <CardList>
                        {filtered.length === 0 && (
                            <Text c="dimmed" fz="sm" ta="center" py="md">
                                No properties found.
                            </Text>
                        )}
                        {filtered.map((property) => (
                            <PropertyCard
                                key={property.propertyId}
                                $active={selectedProperty?.propertyId === property.propertyId}
                            >
                                <PropertyCardName>{property.propertyName}</PropertyCardName>
                                <PropertyStats>
                                    <StatItem>
                                        <StatItemValue>{property.totalUnitCount}</StatItemValue>
                                        <StatItemLabel>Units</StatItemLabel>
                                    </StatItem>
                                    <StatItem>
                                        <StatItemValue>{property.totalSubscriberCount}</StatItemValue>
                                        <StatItemLabel>Subscribers</StatItemLabel>
                                    </StatItem>
                                </PropertyStats>
                                <ViewDetailsBtn onClick={() => callForPropertyDetails(property)}>
                                    View Details
                                </ViewDetailsBtn>
                            </PropertyCard>
                        ))}
                    </CardList>
                </PropertyList>

                {selectedProperty && (
                    <DetailPanel>
                        <DetailPanelHeader>
                            <Text fw={700} fz={14} tt="uppercase" c="#1a1a2e">
                                Apartment Detail: {selectedProperty.name}
                            </Text>
                        </DetailPanelHeader>

                        <DetailStatsGrid>
                            <DetailStatCard>
                                <Text fz={11} fw={600} c="dimmed" tt="uppercase">
                                    Total Active Units (Global)
                                </Text>
                                <Text fz={28} fw={800} c="#1a1a2e">
                                    {detailStats.activeUnits}
                                </Text>
                            </DetailStatCard>
                            <DetailStatCard>
                                <Text fz={11} fw={600} c="dimmed" tt="uppercase">
                                    Total Portfolio Units
                                </Text>
                                <Text fz={28} fw={800} c="#1a1a2e">
                                    {detailStats.totalUnits.toLocaleString()}
                                </Text>
                            </DetailStatCard>
                            <DetailStatCard>
                                <Text fz={11} fw={600} c="dimmed" tt="uppercase">
                                    Penetration Rate
                                </Text>
                                <Text fz={28} fw={800} c="#1a1a2e">
                                    {detailStats.penetrationRate}%
                                </Text>
                            </DetailStatCard>
                        </DetailStatsGrid>

                        <TableWrapper>
                            <Table striped highlightOnHover withTableBorder withColumnBorders>
                                <Table.Thead>
                                    <Table.Tr>
                                        <SortableTh onClick={() => handleSort('customerName')}>
                                            Tenant Name <SortIcon colKey="customerName" />
                                        </SortableTh>
                                        <SortableTh onClick={() => handleSort('unitNumber')}>
                                            Unit # <SortIcon colKey="unitNumber" />
                                        </SortableTh>
                                        <SortableTh onClick={() => handleSort('status')}>
                                            Status <SortIcon colKey="status" />
                                        </SortableTh>
                                        <SortableTh onClick={() => handleSort('subscriptionStartDate')}>
                                            Joining Date <SortIcon colKey="subscriptionStartDate" />
                                        </SortableTh>
                                        <SortableTh onClick={() => handleSort('monthsSince')}>
                                            Months Since Joining <SortIcon colKey="monthsSince" />
                                        </SortableTh>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData.length ? (
                                        sortedData.map((unit) => (
                                            <Table.Tr key={unit.customerId}>
                                                <Table.Td>{unit.customerName}</Table.Td>
                                                <Table.Td>{unit.unitNumber}</Table.Td>
                                                <Table.Td>
                                                    <Badge
                                                        color={STATUS_COLORS[unit.status] || 'gray'}
                                                        variant="light"
                                                        size="sm"
                                                    >
                                                        {unit.status}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>{unit.subscriptionStartDate}</Table.Td>
                                                <Table.Td>{ monthsPassed( unit.subscriptionStartDate ) }</Table.Td>
                                            </Table.Tr>
                                        ))
                                    ) : (
                                        <Table.Tr>
                                            <Table.Td colSpan={5}>
                                                <Text c="dimmed" ta="center" fz="sm" py="md">
                                                    {selectedProperty ? 'No units found.' : 'Select a property to view units.'}
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    )}
                                </Table.Tbody>
                            </Table>
                        </TableWrapper>

                    </DetailPanel>
                )}
            </ContentLayout>
        </PageWrapper>
    );
};

export default Portfolio;

const PageWrapper = styled.div`
    padding: 28px 32px;
    height: 100%;
    overflow-y: auto;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
`;

const ContentLayout = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
`;

const PropertyList = styled.div`
    width: 260px;
    min-width: 260px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SearchWrapper = styled.div``;

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const PropertyCard = styled.div`
    background: ${({ $active }) => ($active ? '#f0f9ff' : '#ffffff')};
    border: 1px solid ${({ $active }) => ($active ? 'var(--blackbox-blue)' : '#e9ecef')};
    border-radius: 8px;
    padding: 16px;
`;

const PropertyCardName = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: #1a1a2e;
    text-transform: uppercase;
    margin: 0 0 12px 0;
`;

const PropertyStats = styled.div`
    display: flex;
    gap: 24px;
    margin-bottom: 14px;
`;

const StatItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatItemValue = styled.span`
    font-size: 24px;
    font-weight: 800;
    color: #1a1a2e;
    line-height: 1;
`;

const StatItemLabel = styled.span`
    font-size: 11px;
    color: #868e96;
    margin-top: 2px;
`;

const ViewDetailsBtn = styled.button`
    width: 100%;
    background-color: #2e7d32;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 8px 0;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Manrope', sans-serif;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #1b5e20;
    }
`;

const DetailPanel = styled.div`
    flex: 1;
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
`;

const DetailPanelHeader = styled.div`
    padding: 14px 16px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
`;

const DetailStatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    border-bottom: 1px solid #e9ecef;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const DetailStatCard = styled.div`
    padding: 16px;
    border-right: 1px solid #e9ecef;

    &:last-child {
        border-right: none;
    }
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
`;

const TableWrapper = styled.div``;

const SortableTh = styled(Table.Th)`
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    & > svg {
        vertical-align: middle;
        margin-left: 4px;
        flex-shrink: 0;
    }

    &:hover {
        background-color: #f1f3f5;
    }
`;
